import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import carrierService from '../../services/carrierService';
import transferService from '../../services/transferService';
import {
  ArrowLeftIcon,
  CurrencyDollarIcon,
  BuildingOfficeIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  CalendarIcon,
  TruckIcon
} from '@heroicons/react/24/outline';

const CarrierFinanceDetail = () => {
  const { carrierId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState({});

  const fetchCarrierFinanceDetails = async () => {
    try {
      setLoading(true);
      const response = await carrierService.getCarrierFinanceDetails(carrierId);
      if (response?.success) {
        setData(response.data);
      }
    } catch (error) {
      console.error('Error fetching carrier finance details:', error);
      toast.error('Taşıyıcı firma detayları yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCarrierFinanceDetails();
  }, [carrierId]);

  const handlePaymentStatusUpdate = async (transferId, newStatus) => {
    try {
      setPaymentLoading(prev => ({ ...prev, [transferId]: true }));

      await transferService.updateTransferPaymentStatus(transferId, {
        carrier_payment_status: newStatus,
        carrier_payment_date: newStatus === 'paid' ? new Date().toISOString() : null
      });

      toast.success('Komisyon durumu güncellendi');
      fetchCarrierFinanceDetails(); // Refresh data
    } catch (error) {
      console.error('Error updating payment status:', error);
      toast.error('Komisyon durumu güncellenirken hata oluştu');
    } finally {
      setPaymentLoading(prev => ({ ...prev, [transferId]: false }));
    }
  };

  const formatCurrency = (amount, currency = 'TRY') => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { color: 'bg-yellow-100 text-yellow-800', text: 'Bekliyor' },
      'confirmed': { color: 'bg-blue-100 text-blue-800', text: 'Onaylandı' },
      'vehicle_assigned': { color: 'bg-indigo-100 text-indigo-800', text: 'Araç Atandı' },
      'on_the_way_to_pickup': { color: 'bg-purple-100 text-purple-800', text: 'Yolda' },
      'arrived_at_pickup': { color: 'bg-orange-100 text-orange-800', text: 'Alış Noktasında' },
      'passenger_on_board': { color: 'bg-teal-100 text-teal-800', text: 'Yolcu Alındı' },
      'completed': { color: 'bg-green-100 text-green-800', text: 'Tamamlandı' },
      'cancelled': { color: 'bg-red-100 text-red-800', text: 'İptal' },
      'cancelled_by_customer': { color: 'bg-red-100 text-red-800', text: 'Müşteri İptali' },
      'cancelled_by_operator': { color: 'bg-red-100 text-red-800', text: 'Operatör İptali' },
      'cancelled_by_driver': { color: 'bg-red-100 text-red-800', text: 'Şoför İptali' },
      'no_show': { color: 'bg-gray-100 text-gray-800', text: 'Gelmedi' }
    };

    const config = statusConfig[status] || statusConfig['pending'];
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const getPaymentStatusBadge = (status) => {
    if (status === 'paid') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircleIcon className="h-3 w-3 mr-1" />
          Ödendi
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
        <ClockIcon className="h-3 w-3 mr-1" />
        Bekliyor
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <XCircleIcon className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <p className="text-gray-500">Taşıyıcı firma bilgileri bulunamadı</p>
      </div>
    );
  }

  const { carrier, transfers, summary } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/finance')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
            </button>
            <BuildingOfficeIcon className="h-8 w-8 text-indigo-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{carrier.name}</h1>
              <p className="text-gray-600">Finans Detayları</p>
            </div>
          </div>
        </div>

        {/* Carrier Info */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {carrier.contact_person && (
            <div>
              <p className="text-sm text-gray-500">Yetkili Kişi</p>
              <p className="font-medium">{carrier.contact_person}</p>
            </div>
          )}
          {carrier.phone && (
            <div>
              <p className="text-sm text-gray-500">Telefon</p>
              <p className="font-medium">{carrier.phone}</p>
            </div>
          )}
          {carrier.email && (
            <div>
              <p className="text-sm text-gray-500">E-posta</p>
              <p className="font-medium">{carrier.email}</p>
            </div>
          )}
        </div>
      </div>

      {/* Financial Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center">
            <TruckIcon className="h-8 w-8 text-blue-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Toplam Transfer</p>
              <p className="text-2xl font-bold text-gray-900">{summary.total_transfers}</p>
            </div>
          </div>
        </div>

        {Object.entries(summary.by_currency).map(([currency, amounts]) => (
          <React.Fragment key={currency}>
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center">
                <CurrencyDollarIcon className="h-8 w-8 text-green-500" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Toplam Komisyon ({currency})</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(amounts.total, currency)}</p>
                </div>
              </div>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center">
                <CheckCircleIcon className="h-8 w-8 text-green-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Ödenen ({currency})</p>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(amounts.paid, currency)}</p>
                </div>
              </div>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center">
                <ClockIcon className="h-8 w-8 text-orange-500" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Bekleyen ({currency})</p>
                  <p className="text-2xl font-bold text-orange-600">{formatCurrency(amounts.pending, currency)}</p>
                </div>
              </div>
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* Currency Breakdown */}
      {Object.keys(summary.by_currency).length > 1 && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Para Birimi Bazında Özet</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(summary.by_currency).map(([currency, amounts]) => (
              <div key={currency} className="border rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">{currency}</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Toplam:</span>
                    <span className="font-medium">{formatCurrency(amounts.total, currency)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Ödenen:</span>
                    <span className="text-green-600">{formatCurrency(amounts.paid, currency)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Bekleyen:</span>
                    <span className="text-orange-600">{formatCurrency(amounts.pending, currency)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Transfers List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Transfer Listesi</h2>
          <p className="text-sm text-gray-600">{transfers.length} transfer listeleniyor</p>
        </div>

        {transfers.length === 0 ? (
          <div className="text-center py-12">
            <TruckIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Henüz transfer bulunmuyor</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rezervasyon
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tarih
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Durum
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Komisyon
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Komisyon Durumu
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transfers.map((transfer) => (
                  <tr key={transfer.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {transfer.reservation?.reservation_number}
                        </div>
                        <div className="text-sm text-gray-500">
                          {transfer.is_return ? 'Dönüş' : 'Varış'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <CalendarIcon className="h-4 w-4 mr-1 text-gray-400" />
                        {formatDate(transfer.transfer_date)}
                      </div>
                      {transfer.transfer_time && (
                        <div className="text-sm text-gray-500">{transfer.transfer_time}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(transfer.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(transfer.commission_amount, transfer.commission_currency)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getPaymentStatusBadge(transfer.commission_status)}
                      {transfer.commission_paid_date && (
                        <div className="text-xs text-gray-500 mt-1">
                          {formatDate(transfer.commission_paid_date)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {transfer.commission_status === 'pending' ? (
                        <button
                          onClick={() => handlePaymentStatusUpdate(transfer.id, 'paid')}
                          disabled={paymentLoading[transfer.id]}
                          className="text-green-600 hover:text-green-900 disabled:opacity-50"
                        >
                          {paymentLoading[transfer.id] ? 'Güncelleniyor...' : 'Ödendi İşaretle'}
                        </button>
                      ) : (
                        <button
                          onClick={() => handlePaymentStatusUpdate(transfer.id, 'pending')}
                          disabled={paymentLoading[transfer.id]}
                          className="text-orange-600 hover:text-orange-900 disabled:opacity-50"
                        >
                          {paymentLoading[transfer.id] ? 'Güncelleniyor...' : 'Bekliyor İşaretle'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CarrierFinanceDetail;
